import {
  buildThreads,
  defaultTextLine,
  phoneBadge,
  textLines,
  type TextLine,
} from "@roof-hawk/core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAsync } from "../../lib/hooks";
import { fetchCalls, fetchPhoneTexts } from "../../lib/queries";
import { useSession } from "../../session";
import type { AppData } from "../shell/Shell";
import { PhonePanel, type PhoneTab } from "./PhonePanel";

/**
 * The phone bubble (ADR 0321).
 *
 * Jonathon, 2026-10-27: "i would prefer the phone feature to be a little icon
 * that constantly hovers in a corner. one that can be minimized." A round
 * button sits in the bottom-right corner of every screen, badged with what is
 * waiting: conversations nobody has answered, and calls missed since Recents
 * was last looked at. Tap it and the phone opens over the page; the dash
 * puts it back. Whether it was open and which tab it was on are remembered
 * across reloads, the way a phone comes back where you left it.
 *
 * This is where the phone's data and its memory live; `PhonePanel` draws it.
 * The bubble is on every screen, so while it is shut it reads only what the
 * badge counts: who texted which way and when, and the calls since Recents
 * was last seen. The whole history, transcripts and all, is read when the
 * phone is opened. Either is read again every couple of minutes while the
 * tab is on screen, and at once when it comes back.
 *
 * When Recents was last looked at belongs to the rep, not the browser: on a
 * shared computer the next rep to sign in has their own missed calls, never
 * a colleague's.
 *
 * The line picked is not remembered at all. Jonathon, 2026-10-30, asked
 * whether a line picked once should stay picked: "12. c", back to the rep's
 * own line each time they close the phone or a conversation. So the pick
 * lasts only while the phone and the conversation it was made in are open,
 * and a reload starts on the rep's own line too.
 */
const OPEN_KEY = "roofboard:phone:open";
const TAB_KEY = "roofboard:phone:tab";
const seenKey = (who: string) => `roofboard:phone:seen:${who}`;

/** How often the badge looks again, between opens. */
const REFRESH_MS = 2 * 60 * 1000;

/** A phone-width screen, where the panel is the whole screen (styles.css). */
const NARROW = 720;

function remembered(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private browsing, or storage full: the phone just forgets.
  }
}

function rememberedTab(): PhoneTab {
  const tab = remembered(TAB_KEY);
  return tab === "recents" || tab === "voicemail" ? tab : "messages";
}

/**
 * When Recents was last looked at, and the line picked since the phone or
 * the conversation was last closed, for whoever is signed in. A different
 * rep signing in reads their own seen time and starts on their own line,
 * never the last one's.
 */
function useRepMemory(who: string | null) {
  const [rememberedFor, setRememberedFor] = useState(who);
  const [seenAt, setSeenAt] = useState<string | null>(() =>
    who ? remembered(seenKey(who)) : null,
  );
  const [pickedLine, setPickedLine] = useState<string | null>(null);
  if (rememberedFor !== who) {
    setRememberedFor(who);
    setSeenAt(who ? remembered(seenKey(who)) : null);
    setPickedLine(null);
  }
  return { seenAt, setSeenAt, pickedLine, setPickedLine };
}

/**
 * Shut, the badge reads only what it counts; open, the panel reads the
 * whole history. Each pair loads only while it is the one on screen, so
 * opening the phone fetches it afresh and closing it lets it go.
 */
function usePhoneData(
  open: boolean,
  seenAt: string | null,
  who: string | null,
) {
  const badgeTexts = useAsync(
    () => (open ? Promise.resolve(null) : fetchPhoneTexts({ brief: true })),
    [open],
  );
  const badgeCalls = useAsync(
    () => (open ? Promise.resolve(null) : fetchCalls({ since: seenAt })),
    [open, who],
  );
  const texts = useAsync(
    () => (open ? fetchPhoneTexts() : Promise.resolve(null)),
    [open],
  );
  const calls = useAsync(
    () => (open ? fetchCalls() : Promise.resolve(null)),
    [open],
  );
  const reloadTexts = open ? texts.reload : badgeTexts.reload;
  const reloadCalls = open ? calls.reload : badgeCalls.reload;

  const refresh = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    await Promise.all([reloadTexts(), reloadCalls()]);
  }, [reloadTexts, reloadCalls]);

  // A hidden tab does not look; coming back to it looks at once.
  useEffect(() => {
    const timer = setInterval(() => void refresh(), REFRESH_MS);
    const onVisible = () => void refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const threads = useMemo(
    () => (texts.data ? buildThreads(texts.data) : []),
    [texts.data],
  );
  const badgeThreads = useMemo(
    () => (badgeTexts.data ? buildThreads(badgeTexts.data) : []),
    [badgeTexts.data],
  );
  return { texts, calls, threads, badgeThreads, badgeCalls };
}

/**
 * The line texts and calls leave from. Our lines come the rep's own first; a
 * pick that is no longer one of them is dropped rather than sent.
 *
 * A call leaves from the line the rep picked, else their own. A rep with no
 * line who picked none calls from the Roof Board line, as before the phone
 * could pick (twilio-call's own fallback), never from whichever colleague's
 * line the picker happens to show first.
 */
function linesInUse(lines: readonly TextLine[], pickedLine: string | null) {
  const picked =
    pickedLine && lines.some((l) => l.number === pickedLine)
      ? pickedLine
      : null;
  return {
    line: picked ?? defaultTextLine(lines),
    callLine: picked ?? lines.find((l) => l.mine)?.number ?? null,
  };
}

/** What the bubble says to a screen reader about what is waiting. */
function waitingLabel(badge: {
  total: number;
  waiting: number;
  missed: number;
}) {
  if (badge.total === 0) return "Nothing waiting";
  const parts: string[] = [];
  if (badge.waiting) {
    parts.push(
      `${badge.waiting} ${badge.waiting === 1 ? "conversation" : "conversations"} waiting on you`,
    );
  }
  if (badge.missed) {
    parts.push(
      `${badge.missed} missed ${badge.missed === 1 ? "call" : "calls"}`,
    );
  }
  return parts.join(", ");
}

export function PhoneBubble({ data }: { data: AppData }) {
  const { profile, people } = useSession();
  const [open, setOpen] = useState(() => remembered(OPEN_KEY) === "1");
  const [tab, setTab] = useState<PhoneTab>(rememberedTab);

  // What this rep left; nobody signed in has nothing remembered.
  const who = profile?.id ?? null;
  const { seenAt, setSeenAt, pickedLine, setPickedLine } = useRepMemory(who);

  const lines = useMemo(() => textLines(people ?? [], who), [people, who]);
  const { line, callLine } = linesInUse(lines, pickedLine);

  const { texts, calls, threads, badgeThreads, badgeCalls } = usePhoneData(
    open,
    seenAt,
    who,
  );
  const badge = phoneBadge({
    threads: badgeThreads,
    calls: badgeCalls.data ?? [],
    seenAt,
  });

  // Looking at Recents or Voicemail is what clears the missed-call count,
  // the way a phone clears its badge when the list is opened.
  const markSeen = useCallback(() => {
    const now = new Date().toISOString();
    setSeenAt(now);
    if (who) remember(seenKey(who), now);
  }, [who, setSeenAt]);

  useEffect(() => {
    if (open && tab !== "messages") markSeen();
  }, [open, tab, markSeen]);

  function show() {
    setOpen(true);
    remember(OPEN_KEY, "1");
  }

  function minimize() {
    setOpen(false);
    remember(OPEN_KEY, null);
    setPickedLine(null);
  }

  function pickTab(next: PhoneTab) {
    setTab(next);
    remember(TAB_KEY, next);
  }

  function pickLine(next: string) {
    setPickedLine(next);
  }

  // On a phone the panel is the whole screen, so going to a job means
  // putting the phone away first; on a desk it stays open beside the page.
  function leave() {
    if (window.innerWidth <= NARROW) minimize();
  }

  if (open) {
    // The full read starts only when the phone opens, so until it lands the
    // lists are not empty, they are not here yet. A refresh after that keeps
    // the list on screen while it looks again.
    return (
      <PhonePanel
        data={data}
        threads={threads}
        textsLoading={texts.data === null && !texts.error}
        textsError={texts.error}
        calls={calls.data ?? []}
        callsLoading={calls.data === null && !calls.error}
        callsError={calls.error}
        onReread={calls.reload}
        tab={tab}
        onTab={pickTab}
        lines={lines}
        line={line}
        callLine={callLine}
        onLine={pickLine}
        onForgetLine={() => setPickedLine(null)}
        onMinimize={minimize}
        onLeave={leave}
      />
    );
  }

  const waiting = waitingLabel(badge);

  return (
    <button
      type="button"
      className="phone-bubble"
      aria-label={`Phone. ${waiting}`}
      title="Phone"
      onClick={show}
    >
      <svg
        viewBox="0 0 24 24"
        width="28"
        height="28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
      </svg>
      {badge.total > 0 ? (
        <span className="phone-badge" aria-hidden="true">
          {badge.total > 99 ? "99+" : badge.total}
        </span>
      ) : null}
    </button>
  );
}
